import React, { FC } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { differenceInSeconds } from 'date-fns';
import { Scan } from '../../../common/model/dto/scan';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import OvDateParser from '../atoms/OvDateParser';
import OvPrettyUserName from '../atoms/OvPrettyUserName';

const SCAN_DATE_FORMAT = 'MM/DD/YYYY - hh:mm A';
const UPDATE_NOTICE_THRESHOLD_SECONDS = 5;

interface OvScanCreatedByProps {
  scan: Scan;
  isUserReadAllowed: boolean;
}

const wasUpdatedAfterCreation = ({ created_at, updated_at }: Scan): boolean =>
  !!created_at &&
  !!updated_at &&
  Math.abs(differenceInSeconds(new Date(created_at), new Date(updated_at))) >
    UPDATE_NOTICE_THRESHOLD_SECONDS;

const OvScanCreatedBy: FC<OvScanCreatedByProps> = ({
  scan,
  isUserReadAllowed,
}) => {
  const { t } = useTranslation();

  const renderUser = () => {
    if (!isUserReadAllowed) {
      return scan.user ? (
        <OvPrettyUserName user={scan.user} options={{ readSafe: true }} />
      ) : null;
    }

    return (
      <UserLink to={`/users/${scan.user?.id ?? scan.user_id}`}>
        {scan.user ? (
          <OvPrettyUserName user={scan.user} />
        ) : (
          t('common.actions.viewProfile')
        )}
      </UserLink>
    );
  };

  return (
    <>
      {renderUser()}
      <DateLine>
        <OvDateParser
          date={scan.created_at as Date}
          format={SCAN_DATE_FORMAT}
        />
      </DateLine>
      {isUserReadAllowed && wasUpdatedAfterCreation(scan) && (
        <DateLine>
          {t('common.updatedAt')}:{' '}
          <OvDateParser
            date={scan.updated_at as Date}
            format={SCAN_DATE_FORMAT}
          />
        </DateLine>
      )}
    </>
  );
};

export default OvScanCreatedBy;

const UserLink = styled(Link)`
  color: ${Colours.OV_BASE};
  text-underline-offset: 3px;

  &:hover {
    color: ${Colours.OV_ACCENT_BLUE};
  }
`;

const DateLine = styled.div`
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
  white-space: nowrap;
`;
